import { Chip, Stack, Typography } from "@mui/material";
import { useDeleteMerchantFacilityMutation } from "Api/Mutations/Merchants/useDeleteMerchantFacilityMutation";
import type { CardListItemType } from "Components/Shared/CardsList/CardsList";
import { ControlledCardsList } from "Components/Shared/CardsList/ControlledCardsList";
import { ModalDialog } from "Components/Shared/Dialogs/ModalDialog";
import { FormSection } from "Components/Shared/FormSection";
import { FacilityDetail } from "Components/Shared/Merchant/Facilities/FacilityDetail";
import { FacilityFormInitializer } from "Components/Shared/Merchant/Facilities/FacilityFormInitializer";
import { isFacilityComplete } from "Components/Shared/Merchant/Facilities/utils";
import type { FacilityFormVariant } from "Components/Shared/Merchant/Facilities/constants";
import type { AddressDto, MerchantFacilityDto } from "Infrastructure/Api/Api";
import type { CurrencyCode } from "Models/CurrencyCodes";
import { getFormattedAddress } from "Utils/AddressUtils";
import { useState } from "react";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { useFormModalClose } from "Hooks/useFormModalClose";

const COMPONENT_RESOURCES = Resources.Components.Merchant.Facilities;

type Props = {
  isReadOnly?: boolean;
  isOnboarding?: boolean;
  variant?: FacilityFormVariant;
  merchantPublicID: string;
  facilities: Array<MerchantFacilityDto>;
  companyHeadquartersAddress?: AddressDto;
  partyPublicID?: string | null;
  // Merchant's market currency — default for a NEW facility's turnover inputs
  // (existing facilities carry their own stored TurnoverCurrencyCode).
  marketCurrencyCode?: CurrencyCode | string;
};

export const Facilities = ({
  isReadOnly,
  isOnboarding,
  variant = "complete",
  facilities,
  merchantPublicID,
  companyHeadquartersAddress,
  partyPublicID,
  marketCurrencyCode,
}: Props) => {
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [selectedFacility, setSelectedFacility] =
    useState<Partial<MerchantFacilityDto> | null>(null);
  const { t } = useResource();

  const handleModalClose = () => {
    setSelectedFacility(null);
    setIsUpdateOpen(false);
    setIsFormDirty(false);
  };

  const { handleCloseRequest, ConfirmDialogComponent } = useFormModalClose({
    isDirty: isFormDirty,
    onClose: handleModalClose,
  });

  const { mutate: deleteFacility, isPending: isDeletePending } =
    useDeleteMerchantFacilityMutation({
      onSuccess: () => {
        toast.success(t(COMPONENT_RESOURCES.DeleteSuccess));
        handleModalClose();
      },
      onError: () => toast.error(t(COMPONENT_RESOURCES.DeleteError)),
    });

  const listItems: Array<CardListItemType> = facilities.map((facility) => {
    const { name, address, publicID } = facility;
    const isIncomplete = isOnboarding && !isFacilityComplete(facility);

    return {
      id: publicID,
      title: (
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography fontWeight="500" fontSize="1rem">
            {name}
          </Typography>
          {isIncomplete && (
            <Chip
              component="span"
              size="small"
              variant="outlined"
              label={t(COMPONENT_RESOURCES.Incomplete)}
              sx={{
                borderColor: "warning.dark",
                color: "warning.dark",
              }}
            />
          )}
        </Stack>
      ),
      subtitle:
        (address && getFormattedAddress(address)) ||
        t(COMPONENT_RESOURCES.NoAddress),
      isDisabled: isDeletePending,
      ...(isReadOnly
        ? {
            onView: () => {
              setSelectedFacility(facility);
              setIsViewOpen(true);
            },
          }
        : {
            onEdit: () => {
              setSelectedFacility(facility);
              setIsUpdateOpen(true);
            },
            onDelete: () =>
              window.confirm(
                t(COMPONENT_RESOURCES.DeleteConfirmation, {
                  name,
                }),
              ) &&
              deleteFacility({
                publicID: merchantPublicID,
                facilityPublicID: facility.publicID,
              }),
          }),
    };
  });

  return (
    <FormSection legend={t(COMPONENT_RESOURCES.Title)}>
      <ControlledCardsList
        listItems={listItems}
        isReadOnly={isReadOnly}
        addButtonConfig={{
          onClick: () => {
            setSelectedFacility(null);
            setIsUpdateOpen(true);
          },
          text: t(COMPONENT_RESOURCES.AddFacility),
        }}
        noItemsText={t(COMPONENT_RESOURCES.NoFacilities)}
      />
      <ModalDialog
        isOpen={isUpdateOpen}
        onClose={handleCloseRequest}
        title={t(
          selectedFacility?.publicID
            ? COMPONENT_RESOURCES.EditFacility
            : COMPONENT_RESOURCES.NewFacility,
        )}
      >
        <FacilityFormInitializer
          facility={selectedFacility || {}}
          merchantPublicID={merchantPublicID}
          onSaveSuccessful={handleModalClose}
          onFacilityCreated={setSelectedFacility}
          onDirtyChange={setIsFormDirty}
          variant={variant}
          companyHeadquartersAddress={companyHeadquartersAddress}
          partyPublicID={partyPublicID}
          marketCurrencyCode={marketCurrencyCode}
        />
      </ModalDialog>
      {ConfirmDialogComponent}
      <ModalDialog
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title={t(COMPONENT_RESOURCES.FacilityDetail)}
      >
        {selectedFacility && (
          <FacilityDetail
            merchantPublicID={merchantPublicID}
            facility={selectedFacility}
          />
        )}
      </ModalDialog>
    </FormSection>
  );
};
